import { useState } from 'react';
import UnitSwitch from '../../../components/unitSwitch/UnitSwitch';
import { s, p } from './AppraisalPrice.styles';
import { t } from '../../../detail/components/Table.styles';
import { TotalSaleList } from '../../data';
import { ReportFormatNumber } from '../../../../func/Function';
import { useReportContext } from '../../../../context/DetailContext';
import styled from 'styled-components';
import ReferenceLink from '../../ReferenceLink';
import AppraiscalModal from './AppraiscalModal';
import LandSaleModal from './LandSaleModal';
import BuildingSaleModal from './BuildingSaleModal';
import ListContent, { ListData } from '../ListContent';
export interface ListItemProps {
  sno: string;
  saddr: string;
  type: string | null;
  areaPyeong: string;
  areaMeter: number;
  status: string | null;
  appraisePricePerMeter: number;
  officialpricePerMeter: number | null;
  appraisePrice: number | null;
  etc: string;
  sinfo?: string;
  data: any;
}

interface Props {
  totalSaleList: TotalSaleList;
  landSaleList: [
    { totalAreaPyeong: number; totalAreaMeter: number; appraisalPrice: number },
    {
      list: ListItemProps[];
      isLandRight: boolean;
    },
  ];
  buildingSaleList: [
    { totalArea: number; totalAreaMeter: number; appraisalPrice: number },
    {
      list: ListItemProps[];
      isLandRight: boolean;
    },
  ];
  appraisalInfo:
    | {
        appraise_date: string;
        appraiser: string;
        lessee_date: string;
      }
    | undefined;
  saleItemInfo: string[];
  a_table: any;
  data?: any;
  linkSet?: {
    buttonLink?: {
      appraisal?: string;
    };
  };
}

type AuctionAppraisalPriceProps = {
  totalSaleList: any;
  landSaleList: any;
  buildingSaleList: any;
  appraisalInfo: any;
  saleItemInfo: string[];
  a_table: any;
  data: any;
  linkSet: any;
};

const AuctionAppraisalPrice = ({
  totalSaleList,
  landSaleList,
  buildingSaleList,
  appraisalInfo,
  saleItemInfo,
  a_table,
  data,
  linkSet,
}: AuctionAppraisalPriceProps) => {
  const [unit, setUnit] = useState('평');
  const { itemType } = useReportContext();
  const [showAppraiscalModal, setShowAppraiscalModal] = useState(false);
  const [showLandSaleModal, setShowLandSaleModal] = useState(false);
  const [showBuildingSaleModal, setShowBuildingSaleModal] = useState(false);

  const aTable = JSON.parse(a_table);
  const isSubHeader = (text: string) => /^\d+\)/.test(text); // 1), 2) ...
  const isSectionHeader = (text: string) => /^\d+\./.test(text); // 1., 2. ...

  const renderAppraisalContent = (aTable: any[]) => {
    if (!aTable) return null;

    const formatBr = (text: string) => {
      // 의미 없는 문자열 '.', '-', 공백만 → null
      const meaninglessPattern = /^[-.\s]*$/;
      if (typeof text !== 'string') return null;

      // 1) 연속 <br/> 하나로
      let t = text.replace(/(<br\s*\/?>\s*){2,}/g, '<br/>');
      // 2) 태그 안의 <br/> 제거
      t = t.replace(/(<[^<>]*?)<br\s*\/?>([^<>]*?>)/g, '$1$2');
      // 3) <br/> → \n
      t = t.replace(/<br\s*\/?>/g, '\n');
      return t;
    };

    return aTable.map((item: any, index: number, array: any[]) => {
      try {
        if (typeof item !== 'string') {
          throw new Error(
            `Invalid item type at index ${index}: ${typeof item}`,
          );
        }

        const text = formatBr(item);
        if (text == null) return null;

        const next =
          typeof array[index + 1] === 'string'
            ? formatBr(array[index + 1])
            : null;
        const meaninglessNext = next == null;

        // 다음 항목이 의미 없으면 헤더도 출력하지 않음
        if ((isSectionHeader(item) || isSubHeader(item)) && meaninglessNext)
          return null;

        if (isSectionHeader(item)) {
          return <s.SectionHeader key={index}>{text}</s.SectionHeader>;
        }
        if (isSubHeader(item)) {
          return <s.SubHeader key={index}>{text}</s.SubHeader>;
        }
        return <s.ContentDescrip key={index}>{text}</s.ContentDescrip>;
      } catch (error) {
        console.error(`Error processing item at index ${index}:`, error);
        return <s.ContentDescrip key={index}>{String(item)}</s.ContentDescrip>;
      }
    });
  };

  const firstLand = landSaleList?.[1]?.list?.[0];
  const firstBuilding = buildingSaleList?.[1]?.list?.[0];

  const landListData: ListData[] = firstLand
    ? landSaleList[1].isLandRight
      ? [
          {
            label: '구분',
            value: 1,
          },
          {
            label: '면적',
            value: Number(firstLand.areaMeter).toFixed(1),
            isArea: true,
            value_p: Number(firstLand.areaPyeong).toFixed(1),
            hasToggleButton: true,
          },
          {
            label: 'm²당 단가',
            value: ReportFormatNumber(firstLand.appraisePricePerMeter),
          },
          {
            label: '감정가',
            value: ReportFormatNumber(Number(firstLand.appraisePrice)),
          },
        ]
      : [
          {
            label: '구분',
            value: 1,
          },
          {
            label: '지번',
            value: firstLand.saddr,
          },
          {
            label: '용도/구조/면적/토지이용계획',
            value: `${firstLand.status ?? '-'} 
${Number(firstLand.areaMeter).toFixed(1)}m²(${Number(
              firstLand.areaPyeong,
            ).toFixed(1)}평)`,
          },
          {
            label: 'm²당 단가',
            value: ReportFormatNumber(firstLand.appraisePricePerMeter),
          },
          {
            label: '감정가',
            value: ReportFormatNumber(Number(firstLand.appraisePrice)),
          },
        ]
    : [];

  const buildingListData: ListData[] = firstBuilding
    ? buildingSaleList[1].isLandRight
      ? [
          {
            label: '구분',
            value: 1,
          },
          {
            label: '지번',
            value: firstBuilding.saddr,
          },
          {
            label: '면적(전용)',
            value: Number(firstBuilding.areaMeter).toFixed(1),
            isArea: true,
            value_p: Number(firstBuilding.areaPyeong).toFixed(1),
            hasToggleButton: true,
          },
          {
            label: '감정가',
            value: ReportFormatNumber(Number(firstBuilding.appraisePrice)),
          },
        ]
      : [
          {
            label: '구분',
            value: 1,
          },
          {
            label: '지번',
            value: firstBuilding.saddr,
          },
          {
            label: '용도/구조/면적/토지이용계획',
            value: `${firstBuilding.sinfo ?? '-'} / ${firstBuilding.status ?? '-'} 
           ${Number(firstBuilding.areaMeter).toFixed(1)}m²(${Number(
             firstBuilding.areaPyeong,
           ).toFixed(1)}평)`,
          },
          {
            label: '감정가',
            value: ReportFormatNumber(Number(firstBuilding.appraisePrice)),
          },
        ]
    : [];

  return (
    <Container id="감정가">
      {/* 감정가 써머리 */}
      <Content $gap={16}>
        <TitleBox>
          감정가
          {linkSet?.buttonLink?.appraisal && (
            <AttachButton
              onClick={() => {
                if (linkSet?.buttonLink?.appraisal) {
                  window.open(linkSet.buttonLink.appraisal, '_blank');
                }
              }}
            >
              <img src="/assets/images/icon/pdf.svg" alt="첨부파일" />
              감정평가서 PDF {'>'}
            </AttachButton>
          )}
        </TitleBox>
        <ListItemColorBox>
          <ListItem>
            <span>감정원</span>
            {appraisalInfo.appraiser}
          </ListItem>
          <ListItem>
            <span>가격시점</span>
            {appraisalInfo?.appraise_date?.includes(':')
              ? appraisalInfo.appraise_date.split('/')[0]?.split(':')[1]?.trim()
              : ''}
          </ListItem>
          <ListItem>
            <span>보존등기일</span>
            {appraisalInfo?.appraise_date?.includes('/')
              ? appraisalInfo.appraise_date.split('/')[1]?.split(':')[1]?.trim()
              : '-'}
          </ListItem>
        </ListItemColorBox>

        {a_table && (
          <Content $gap={12}>
            <SummaryBox>
              <SummaryBoxTitle>감정평가서 요약</SummaryBoxTitle>
              {renderAppraisalContent(aTable)}
            </SummaryBox>
            <MoreButtonContainer>
              <Divider />
              <MoreButton onClick={() => setShowAppraiscalModal(true)}>
                감정평가서 요약 더보기 {'>'}
              </MoreButton>
              <Divider />
            </MoreButtonContainer>
          </Content>
        )}
      </Content>
      {/* 전체감정가 */}
      <s.Block>
        <s.BlockTitleBox>
          <s.BlockTitle>
            <img src="/assets/images/icon/money.svg" alt="" />
            <span>전체 감정가</span>
          </s.BlockTitle>
          <UnitSwitch unit={unit} setUnit={setUnit} icon />
        </s.BlockTitleBox>
        <t.Table>
          <thead>
            <t.Row>
              <t.TableHeadTh width="72px">전체목록</t.TableHeadTh>
              <t.TableHeadTh width="110px">면적</t.TableHeadTh>
              <t.TableHeadTh width="144px">감정가</t.TableHeadTh>
            </t.Row>
          </thead>
          <tbody>
            <t.Row>
              <t.ContentCell width="72px" border={true} textAlign="center">
                토지{' '}
              </t.ContentCell>
              <t.ContentCell width="110px" border={true} textAlign="center">
                {unit === '평'
                  ? `${totalSaleList.landAreaPyeong.toFixed(1)}평`
                  : `${totalSaleList.landAreaMeter.toFixed(1)}m²`}
              </t.ContentCell>
              <t.ContentCell width="144px" border={true} textAlign="center">
                {ReportFormatNumber(totalSaleList.landPrice)}
              </t.ContentCell>
            </t.Row>
            <t.Row>
              <t.ContentCell width="72px" border={true} textAlign="center">
                건물
                <br />
                (전용)
              </t.ContentCell>
              <t.ContentCell width="110px" border={true} textAlign="center">
                {unit === '평'
                  ? `${totalSaleList.buildingAreaPyeong.toFixed(1)}평`
                  : `${totalSaleList.buildingAreaMeter.toFixed(1)}m²`}
              </t.ContentCell>
              <t.ContentCell width="144px" border={true} textAlign="center">
                {ReportFormatNumber(totalSaleList.landPrice)}
              </t.ContentCell>
            </t.Row>
            <t.Row>
              <t.TitleCell
                width="auto"
                colSpan={3}
                style={{ backgroundColor: '#008AFF0F' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span style={{ color: '#222' }}>감정가합계</span>
                  <span style={{ fontWeight: 700, color: '#008AFF' }}>
                    {ReportFormatNumber(
                      totalSaleList.landPrice + totalSaleList.buildingPrice,
                    )}
                  </span>
                </div>
              </t.TitleCell>
            </t.Row>
          </tbody>
        </t.Table>
      </s.Block>
      {/* 토지감정가 */}
      <s.Block>
        <s.BlockTitleBox>
          <s.BlockTitle>
            <img src="/assets/images/icon/forest.svg" alt="" />
            <span>토지 감정가</span>
          </s.BlockTitle>
        </s.BlockTitleBox>
        <ListItemColorBox>
          <ListItem>
            <span>면적소계</span>
            {landSaleList[0].totalAreaMeter.toFixed(1)}m² (
            {landSaleList[0].totalAreaPyeong.toFixed(1)}평)
          </ListItem>
          <ListItem>
            <span>감정가소계</span>
            {ReportFormatNumber(landSaleList[0].appraisalPrice)}
          </ListItem>
        </ListItemColorBox>
        <ListContent data={landListData} />
        {landSaleList[1].list.length > 1 && (
          <MoreButtonContainer>
            <Divider />
            <MoreButton onClick={() => setShowLandSaleModal(true)}>
              토지 감정가 더보기 {'>'}
            </MoreButton>
            <Divider />
          </MoreButtonContainer>
        )}
      </s.Block>
      {/* 건물 감정가 */}
      {itemType === 'H' && (
        <s.Block>
          <s.BlockTitleBox>
            <s.BlockTitle>
              <img src="/assets/images/icon/building.svg" alt="" />
              <span>건물 감정가</span>
            </s.BlockTitle>
          </s.BlockTitleBox>
          <ListItemColorBox>
            <ListItem>
              <span>면적소계</span>
              {buildingSaleList[0].totalAreaMeter.toFixed(1)}m² (
              {buildingSaleList[0].totalArea.toFixed(1)}평)
            </ListItem>
            <ListItem>
              <span>감정가소계</span>
              {ReportFormatNumber(buildingSaleList[0].appraisalPrice)}
            </ListItem>
          </ListItemColorBox>
          <ListContent data={buildingListData} />
          {buildingSaleList[1].list.length > 1 && (
            <MoreButtonContainer>
              <Divider />
              <MoreButton onClick={() => setShowBuildingSaleModal(true)}>
                건물 감정가 더보기 {'>'}
              </MoreButton>
              <Divider />
            </MoreButtonContainer>
          )}
        </s.Block>
      )}
      {/* <GrayBox>
        <t.NoteTitle>이용현황</t.NoteTitle>
        <div>
          {saleItemInfo.map((el) => {
            return el.split('*').map((el) => {
              if (el.trim() === '' || !el) {
                return '';
              }
              return (
                <Info key={el}>
                  <div>・</div>
                  {el}
                </Info>
              );
            });
          })}
        </div>
      </GrayBox> */}

      {showAppraiscalModal && (
        <AppraiscalModal
          aTable={aTable}
          onClose={() => setShowAppraiscalModal(false)}
        />
      )}
      {showLandSaleModal && (
        <LandSaleModal
          landSaleList={landSaleList}
          onClose={() => setShowLandSaleModal(false)}
        />
      )}
      {showBuildingSaleModal && (
        <BuildingSaleModal
          buildingSaleList={buildingSaleList}
          onClose={() => setShowBuildingSaleModal(false)}
        />
      )}
    </Container>
  );
};

export default AuctionAppraisalPrice;

export const PAuctionAppraisalPrice = ({ data, link, linkSet }: any) => {
  const pAuctionInfoArray = JSON.parse(data);
  const flattenedArray = pAuctionInfoArray.flat();

  const appraisalData: ListData[] = [
    { label: '감정평가기관', value: flattenedArray[0] },
    { label: '평가일', value: flattenedArray[1] },
    { label: '평가금액', value: flattenedArray[2] },
  ];
  return (
    <p.PContainer id="감정가">
      <TitleBox style={{ fontSize: '16px', fontWeight: '700' }}>
        감정가
        {/* {linkSet?.buttonLink?.appraisal && (
          <AttachButton
            onClick={() => {
              if (linkSet?.buttonLink?.appraisal) {
                window.open(linkSet.buttonLink.appraisal, '_blank');
              }
            }}
          >
            <img src="/assets/images/icon/pdf.svg" alt="" />
            감정평가서 PDF {'>'}
          </AttachButton>
        )} */}
      </TitleBox>
      <ListContent data={appraisalData} />
    </p.PContainer>
  );
};

const TableWrapper = styled.div`
  border: 1px solid #e1e2e4;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
  background-color: #fff;
  gap: 24px;
`;

const Content = styled.div<{ $gap?: number; $center?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap && `${$gap}px`};
  align-items: ${({ $center }) => $center && 'center'};
`;

const AttachButton = styled.div`
  display: flex;
  flex-direction: row;
  border-radius: 8px;
  padding-top: 5px;
  padding-right: 10px;
  padding-bottom: 5px;
  padding-left: 8px;
  gap: 4px;
  background-color: #0220470d;
  align-items: center;

  font-weight: 500;
  font-size: 12px;
  color: #000c1ecc;
`;

const TitleBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  font-weight: 700;
  font-size: 16px;
`;

const PDFButton = styled.button`
  display: flex;
  align-items: center;
  border-radius: 8px;
  padding: 5px 10px 5px 8px;
  gap: 4px;
  background: #0220470d;
  border: none;

  font-weight: 500;
  font-size: 12px;
  color: #000c1ecc;
`;

const AttachBox = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #008aff;
`;

const ListItemColorBox = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  padding: 10px 16px;
  gap: 8px;
  background: #f7f8fc;
`;

const ListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
  gap: 8px;

  span {
    font-size: 12px;
    color: #616161;
  }
`;

const DataListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
  text-align: end;
  gap: 8px;

  span {
    white-space: nowrap;
    text-align: start;
    font-size: 13px;
    color: #616161;
  }
`;

const SummaryBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  gap: 8px;
  padding: 16px;
  background: #f7f8fc;

  max-height: 240px;
  overflow: hidden;

  /* 아래쪽이 점점 사라지는 마스크 */
  -webkit-mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 1) 40%,
    rgba(0, 0, 0, 0) 100%
  );
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: top;
  -webkit-mask-size: 100% 100%;

  mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 1) 40%,
    rgba(0, 0, 0, 0) 100%
  );
  mask-repeat: no-repeat;
  mask-position: top;
  mask-size: 100% 100%;
`;

const SummaryBoxTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
`;

const MoreButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #f7f7f9;
`;

const MoreButton = styled.button`
  border-radius: 9999px;
  padding: 8px 20px;
  background: #f7f7f9;
  border: none;
  white-space: nowrap;

  font-weight: 500;
  font-size: 12px;
  color: #4e5968;
`;

const GrayBox = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  padding: 12px 16px;
  gap: 6px;
  background: #f7f8fc;

  font-weight: 500;
  font-size: 12px;
  color: #37383c9c;
`;

const Info = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2px;
  line-height: 145%;
`;
